import { hasBannedCharacters } from '../../lib/validation/text_validation';

export const RESOURCE_FILE_TYPES = [
  'Image',
  'Excel',
  'Audio',
  'Video',
  'CSV',
  'Text File',
  'Word Doc',
  'Power Point',
  'PDF'
] as const;

export const DEFAULT_RESOURCE_CATEGORY_OPTIONS = [
  'Platform Training',
  'Standards and Frameworks',
  'Programs and Initiatives',
  'Technical Training',
  'Other'
] as const;

export const ALLOWED_FILE_EXTENSIONS = [
  '.jpg',
  '.png',
  '.webp',
  '.gif',
  '.xls',
  '.xlsx',
  '.mp3',
  '.wav',
  '.aac',
  '.flac',
  '.mp4',
  '.avi',
  '.csv',
  '.txt',
  '.rtf',
  '.html',
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.pdf',
  '.link'
];

/**
 * Validates a resource URI string by ensuring it is non-empty and contains only allowed characters.
 * @param {unknown} uri The resource URI string to validate.
 * @returns {boolean} `true` if the URI is valid, `false` otherwise.
 */
export const isValidResourceUri = (uri: unknown): boolean =>
  typeof uri === 'string' && !!(uri && /^[a-zA-Z0-9/_\-.()]+$/.test(uri));

/**
 * Validates a resource name string by ensuring it is non-empty and does not contain banned characters.
 * @param {unknown} name The resource name string to validate.
 * @returns {boolean} `true` if the name is valid, `false` otherwise.
 */
export const isValidResourceName = (name: unknown): boolean =>
  typeof name === 'string' &&
  !!(name && !hasBannedCharacters(name, { banned_characters: "_,.'&$+%@" }));

/**
 * Validates a resource file type string by ensuring it matches one of the supported file type values.
 * @param {unknown} file_type The resource file type string to validate.
 * @returns {boolean} `true` if the file type is valid, `false` otherwise.
 */
export const isValidResourceFileType = (file_type: unknown): boolean =>
  typeof file_type === 'string' &&
  !!(file_type && (RESOURCE_FILE_TYPES as ReadonlyArray<string>).includes(file_type));

/**
 * Validates a list of resource tags by ensuring it is a non-empty array of strings with no duplicates and no banned characters.
 * @param {unknown} tags The resource tags to validate.
 * @returns {boolean} `true` if the tags are valid, `false` otherwise.
 */
export const isValidResourceTags = (tags: unknown): boolean =>
  Array.isArray(tags) &&
  tags.length > 0 &&
  new Set(tags).size === tags.length &&
  tags.every(
    (tag) =>
      typeof tag === 'string' &&
      tag &&
      !hasBannedCharacters(tag, { banned_characters: "-_,.()'&$+%@" })
  );

/**
 * Validates a list of resource categories by ensuring it is a non-empty array with no duplicates and all values are allowed options.
 * @param {unknown} categories The resource categories to validate.
 * @returns {boolean} `true` if the categories are valid, `false` otherwise.
 */
export const isValidResourceCategories = (categories: unknown): boolean =>
  Array.isArray(categories) &&
  categories.length > 0 &&
  new Set(categories).size === categories.length &&
  categories.every((c) => (DEFAULT_RESOURCE_CATEGORY_OPTIONS as ReadonlyArray<string>).includes(c));

/**
 * Validates a resource file size string by ensuring it is non-empty and represents a valid non-negative number.
 * @param {unknown} size The file size string to validate.
 * @returns {boolean} `true` if the size is valid, `false` otherwise.
 */
export const isValidResourceFileSize = (size: unknown): boolean =>
  typeof size === 'string' && !!(size && !Number.isNaN(Number(size)) && Number(size) >= 0);

/**
 * Validates a resource source string by ensuring it is non-empty and does not contain banned characters.
 * @param {unknown} source The resource source string to validate.
 * @returns {boolean} `true` if the source is valid, `false` otherwise.
 */
export const isValidResourceSource = (source: unknown): boolean =>
  typeof source === 'string' &&
  !!(source && !hasBannedCharacters(source, { banned_characters: "0123456789-_,.()'&$+%@" }));

/**
 * Validates a resource description string by ensuring it is non-empty and does not exceed 1000 characters.
 * @param {unknown} description The resource description string to validate.
 * @returns {boolean} `true` if the description is valid, `false` otherwise.
 */
export const isValidResourceDescription = (description: unknown): boolean =>
  typeof description === 'string' &&
  !!(description && description.length > 0 && description.length <= 1000);

/**
 * Validates a file extension string by ensuring it is non-empty and matches one of the allowed file extensions.
 * @param {unknown} file_extension The file extension string to validate.
 * @returns {boolean} `true` if the file extension is valid, `false` otherwise.
 */
export const isValidFileExtension = (file_extension: unknown): boolean =>
  typeof file_extension === 'string' &&
  !!(file_extension && ALLOWED_FILE_EXTENSIONS.includes(file_extension.toLowerCase()));

/**
 * Validates a resource alias string by ensuring it is non-empty and does not contain banned characters.
 * @param {unknown} alias The resource alias string to validate.
 * @returns {boolean} `true` if the alias is valid, `false` otherwise.
 */
export const isValidResourceAlias = (alias: unknown): boolean =>
  typeof alias === 'string' &&
  !!(alias && !hasBannedCharacters(alias, { banned_characters: "_,.'&$+%@" }));
